/**
 * Vocabulaire du bilan pédiatrique (nourrisson de moins d'un an), repris de la V1.
 *
 * Chaque liste sert deux fois : au schéma de validation, qui refuse toute autre valeur, et à
 * l'écran, qui en tire ses cases et ses options dans cet ordre. Les libellés vivent dans les
 * traductions (`pediatric:*`), jamais ici.
 *
 * Retirés de la V1 : les blocs `behavior` et `currentHealth`, déclarés « compatibilité
 * descendante » mais rendus et lus nulle part, et une dizaine de champs jamais affichés
 * (`fallDetails`, `vaccinationDate`, `doctorName`…).
 */

/* Module A — données statiques ------------------------------------------- */

export const pediatricFamilyConditions = [
  'asthma',
  'diabetes',
  'allergies',
  'heartDisease',
  'cancer',
  'epilepsy',
  'kidneyProblems',
] as const;
export type PediatricFamilyCondition = (typeof pediatricFamilyConditions)[number];

export const pediatricPregnancyComplications = [
  'feverOrRash',
  'groupBStrep',
  'gestationalDiabetes',
  'highBloodPressure',
  'anemia',
  'infections',
] as const;
export type PediatricPregnancyComplication = (typeof pediatricPregnancyComplications)[number];

export const pediatricPregnancyEvents = [
  'contractions',
  'immobilization',
  'stripping',
  'accident',
  'illness',
  'tranquilizers',
] as const;
export type PediatricPregnancyEvent = (typeof pediatricPregnancyEvents)[number];

export const pediatricDeliveryTypes = ['vaginal', 'cesarean'] as const;
export type PediatricDeliveryType = (typeof pediatricDeliveryTypes)[number];
export const pediatricTerms = ['on_time', 'premature', 'late'] as const;
export type PediatricTerm = (typeof pediatricTerms)[number];
export const pediatricAnesthesias = ['none', 'epidural', 'spinal'] as const;

export const pediatricBirthComplications = [
  'forceps',
  'vacuum',
  'abdominalExpression',
  'episiotomy',
  'fingerInMouth',
] as const;
export type PediatricBirthComplication = (typeof pediatricBirthComplications)[number];

export const pediatricNeonatalFeedingTypes = [
  'breast_direct',
  'breast_bottle',
  'formula',
  'mixed',
] as const;

export const pediatricNeonatalSigns = [
  'cyanosed',
  'resuscitation',
  'criedImmediately',
  'deformedSkull',
] as const;
export type PediatricNeonatalSign = (typeof pediatricNeonatalSigns)[number];

/** Événements de la première semaine sans précision : une simple case chacun. */
export const pediatricFirstWeekSimpleEvents = [
  'cyanosis',
  'fever',
  'breathingTrouble',
  'diarrhea',
] as const;
export type PediatricFirstWeekSimpleEvent = (typeof pediatricFirstWeekSimpleEvents)[number];

export const pediatricOxygenTypes = ['nasal', 'cpap', 'ventilator'] as const;
export const pediatricTransfusionTypes = ['exchange', 'simple'] as const;

/* Module B — développement et alimentation -------------------------------- */

export const pediatricMilestones = [
  'holdsHead',
  'rollsOver',
  'sitsAlone',
  'crawls',
  'fourLegged',
  'standsUp',
  'firstSteps',
  'babbles',
  'firstWords',
] as const;
export type PediatricMilestone = (typeof pediatricMilestones)[number];

/**
 * Les deux familles de la fenêtre « Bilan pédiatrique — 1ère année » de la V1 : sept acquisitions
 * motrices, deux de langage. Les trois signes d'interaction sociale forment la troisième.
 */
export const pediatricMilestoneGroups = {
  motor: ['holdsHead', 'rollsOver', 'sitsAlone', 'crawls', 'fourLegged', 'standsUp', 'firstSteps'],
  language: ['babbles', 'firstWords'],
} as const satisfies Record<string, readonly PediatricMilestone[]>;
export type PediatricMilestoneGroup = keyof typeof pediatricMilestoneGroups;

export const pediatricSocialSigns = ['smiles', 'eyeContact', 'respondsToName'] as const;
export type PediatricSocialSign = (typeof pediatricSocialSigns)[number];

export const pediatricFeedingTypes = [
  'exclusive_breast',
  'formula',
  'mixed',
  'diversified',
] as const;
export type PediatricFeedingType = (typeof pediatricFeedingTypes)[number];
export const pediatricInitialFeedingTypes = ['exclusive_breast', 'formula', 'mixed'] as const;
export type PediatricInitialFeedingType = (typeof pediatricInitialFeedingTypes)[number];
export const pediatricFeedingRegurgitations = ['none', 'rare', 'frequent', 'systematic'] as const;

/* Module C — observations de la visite ------------------------------------ */

export const pediatricSymptoms = [
  'colics',
  'reflux',
  'sleepDisorders',
  'excessiveCrying',
  'feedingDifficulties',
  'torticollis',
  'plagiocephaly',
  'postureAsymmetry',
  'irritability',
  'constipation',
  'other',
] as const;
export type PediatricSymptom = (typeof pediatricSymptoms)[number];

export const pediatricSymptomOnsets = ['sudden', 'progressive', 'since_birth'] as const;
export const pediatricRecentEvents = ['fall', 'illness', 'vaccination'] as const;
export type PediatricRecentEvent = (typeof pediatricRecentEvents)[number];
export const pediatricReferralSources = [
  'self',
  'pediatrician',
  'midwife',
  'maternity',
  'other_practitioner',
] as const;

export const pediatricBehaviors = ['calm', 'normal', 'agitated', 'irritable', 'apathetic'] as const;
export const pediatricSleepQualities = ['good', 'variable', 'poor', 'very_poor'] as const;
export const pediatricSleepLocations = ['own_room', 'co_sleeping', 'parents_bed'] as const;
export const pediatricVisitRegurgitations = [
  'none',
  'occasional',
  'frequent',
  'systematic',
] as const;
export const pediatricStoolConsistencies = ['normal', 'liquid', 'hard', 'variable'] as const;
export const pediatricStoolColors = ['normal', 'green', 'pale', 'bloody'] as const;

export const pediatricFontanelleStates = ['normal', 'bulging', 'sunken', 'closed'] as const;
export const pediatricSutureStates = ['normal', 'overriding', 'separated'] as const;
export const pediatricCranialSymmetries = [
  'symmetric',
  'asymmetric_right',
  'asymmetric_left',
] as const;
export const pediatricSides = ['right', 'left'] as const;
export const pediatricRotationPreferences = ['none', 'right', 'left'] as const;
export const pediatricSpineAlignments = ['normal', 'scoliotic_tendency', 'kyphotic'] as const;
export const pediatricAbdominalPalpations = [
  'soft_relaxed',
  'tense',
  'distended',
  'painful',
] as const;
export const pediatricDiaphragmMobilities = ['normal', 'restricted', 'asymmetric'] as const;
export const pediatricGlobalTones = ['normal', 'hypotonic', 'hypertonic', 'variable'] as const;
export const pediatricAxialTones = ['normal', 'weak', 'strong'] as const;
export const pediatricLimbTones = ['normal', 'hypotonic', 'hypertonic', 'asymmetric'] as const;

export const pediatricReflexes = ['moro', 'grasp', 'sucking', 'rooting', 'tonicNeck'] as const;
export type PediatricReflex = (typeof pediatricReflexes)[number];
/** Une seule échelle pour les cinq réflexes, comme l'écran de la V1. */
export const pediatricReflexStates = [
  'present',
  'absent',
  'weak',
  'asymmetric',
  'not_tested',
] as const;

/** Sévérité de la plagiocéphalie, lue sur le CVAI. */
export const plagiocephalySeverities = [
  'normal',
  'mild',
  'moderate',
  'severe',
  'very_severe',
] as const;
export type PlagiocephalySeverity = (typeof plagiocephalySeverities)[number];

/** Sévérité de la brachycéphalie, lue sur l'indice céphalique. */
export const brachycephalySeverities = ['normal', 'mild', 'moderate', 'severe'] as const;
export type BrachycephalySeverity = (typeof brachycephalySeverities)[number];

export const pediatricAlertSigns = [
  'fever',
  'weightLoss',
  'bulgingFontanelle',
  'sunkenFontanelle',
  'persistentVomiting',
  'bloodyStool',
  'dehydration',
  'lethargy',
  'abnormalCry',
  'breathingDifficulty',
  'skinColorChange',
  'seizures',
  'noEyeContact',
  'developmentalRegression',
  'hipAsymmetry',
] as const;
export type PediatricAlertSign = (typeof pediatricAlertSigns)[number];

export const pediatricReferralUrgencies = ['routine', 'soon', 'urgent'] as const;
export type PediatricReferralUrgency = (typeof pediatricReferralUrgencies)[number];
export const pediatricReferralTargets = [
  'pediatrician',
  'emergency',
  'specialist',
  'physiotherapist',
  'other',
] as const;
export type PediatricReferralTarget = (typeof pediatricReferralTargets)[number];
